import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getKafDomain, type KafLessonMeta } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { SpiralJourney, type SpiralPoint } from "@/academy/engines/living/SpiralJourney";
import { ArrowRight, Clock } from "lucide-react";
import { useMemo, useState } from "react";
import { NOINDEX_META, breadcrumbs, courseNode, ldScript, seoMeta } from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/academy/kafrequency/$domain/")({
  loader: async ({ params }) => {
    const data = await getKafDomain({ data: { slug: params.domain } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Domain unavailable — KAfrequency Academy" }, NOINDEX_META] };
    }
    const d = loaderData.domain;
    const path = `/academy/kafrequency/${params.domain}`;
    const title = `${d.name} — KAfrequency Academy | Buddha KA`;
    const description =
      d.description ?? d.tagline ?? `${d.name}: a living domain of the KAfrequency Academy.`;
    return {
      ...seoMeta({ title, description, path }),
      scripts: [
        ldScript(
          courseNode({
            id: `${absoluteUrl(path)}#course`,
            url: absoluteUrl(path),
            name: d.name,
            description,
            partOfCourseId: `${absoluteUrl("/academy/kafrequency")}#course`,
            lessons: loaderData.lessons.map((l) => ({
              path: `/academy/kafrequency/${params.domain}/lesson/${l.slug}`,
              name: l.title,
            })),
          }),
        ),
        ldScript(
          breadcrumbs([
            ["Academy", "/academy"],
            ["KAfrequency", "/academy/kafrequency"],
            [d.name, path],
          ]),
        ),
      ],
    };
  },

  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Domain not found</h1>
      <Link to="/academy/kafrequency" className="mt-4 inline-block text-gold">Back to the spiral</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="text-sm text-foreground/60">{(error as Error).message}</p>
    </div>
  ),
  component: DomainPage,
});

function DomainPage() {
  const initial = Route.useLoaderData();
  const { data } = useQuery({
    queryKey: ["kaf", "domain", initial.domain.slug],
    queryFn: () => getKafDomain({ data: { slug: initial.domain.slug } }),
    initialData: initial,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  if (!data) return null;
  const { domain } = data;
  const lessons = data.lessons as KafLessonMeta[];

  const points = useMemo<SpiralPoint[]>(
    () => lessons.map((l, i) => ({ id: l.id, slug: l.slug, label: l.title, hue: domain.hue, order: l.order_index || i + 1 })),
    [lessons, domain.hue],
  );
  const selectedLesson = selectedId ? lessons.find((l) => l.id === selectedId) : null;

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0 opacity-70"><LivingBackdrop hue={domain.hue} density={90} /></div>
      <section className="relative z-10 mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <Link to="/academy/kafrequency" className="text-xs uppercase tracking-[0.3em] text-gold/80 hover:text-gold">← Spiral</Link>
        <p className="mt-4 text-xs uppercase tracking-[0.3em]" style={{ color: `hsl(${domain.hue} 85% 65%)` }}>{domain.tagline}</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-gold-luminous">{domain.name}</h1>
        <p className="mt-4 max-w-2xl text-foreground/75">{domain.description}</p>
      </section>

      <section className="relative z-10 mx-auto grid max-w-6xl gap-8 px-4 pb-16 sm:px-6 lg:grid-cols-[1fr,340px]">
        <div>
          <SpiralJourney points={points} onSelect={(p) => setSelectedId(p.id)} selectedId={selectedId} height={520} />
        </div>
        <aside className="rounded-2xl border hairline bg-obsidian/70 p-5">
          {selectedLesson ? (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Lesson</div>
              <h2 className="mt-2 font-serif text-xl text-gold-luminous">{selectedLesson.title}</h2>
              {selectedLesson.summary && <p className="mt-2 text-sm text-foreground/75">{selectedLesson.summary}</p>}
              <div className="mt-3 flex items-center gap-3 text-xs text-foreground/60">
                <span className="uppercase tracking-widest">{selectedLesson.difficulty}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {selectedLesson.est_minutes} min</span>
              </div>
              <Link
                to="/academy/kafrequency/$domain/lesson/$lesson"
                params={{ domain: domain.slug, lesson: selectedLesson.slug }}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian"
              >
                Begin lesson <ArrowRight size={14} />
              </Link>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Lessons</div>
              <div className="mt-3 flex flex-col gap-2">
                {lessons.map((l) => (
                  <button key={l.id} onClick={() => setSelectedId(l.id)}
                    className="rounded-lg border hairline px-3 py-2 text-left text-sm hover:border-gold/60">
                    <div className="font-serif text-gold-luminous">{l.title}</div>
                    {l.summary && <div className="text-xs text-foreground/60">{l.summary}</div>}
                  </button>
                ))}
              </div>
            </>
          )}
        </aside>
      </section>
    </div>
  );
}
